import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import React, { CSSProperties, ReactNode, useState } from "react";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import { SlidersHorizontal } from "lucide-react";
import { FiltersGroupProps } from "@shared/features/filters/types";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import Drawer from "common/Drawers/Drawer";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { FILTERS_DRAWER_BUTTON_ID } from "testIds";
import { getNewLookChipColors } from "common/Filters/common/styles.ts";

interface NewLookFiltersBarProps
  extends Pick<
    FiltersGroupProps,
    | "filtersActions"
    | "filtersSelectors"
    | "hideFilterTypes"
    | "filterTypeToTitle"
  > {
  /** Rendered after the filters button, e.g. the sort pill on phones. */
  extraComponent?: ReactNode;
  /** Passed to the chips row (phones pad it inside the page tabs). */
  style?: CSSProperties;
  className?: string;
}

const NewLookFiltersBar = ({
  filtersActions,
  filtersSelectors,
  hideFilterTypes,
  filterTypeToTitle,
  extraComponent,
  style,
  className,
}: NewLookFiltersBarProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const numActiveFilters = useSelector(filtersSelectors.selectNumActiveFilters);

  const filtersButton = (
    <FiltersButton
      type="button"
      data-testid={FILTERS_DRAWER_BUTTON_ID}
      aria-label={t("filters.common.title")}
      title={t("filters.common.title")}
      $active={numActiveFilters > 0}
      onClick={() => setDrawerOpen(true)}
    >
      <SlidersHorizontal size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
      {numActiveFilters > 0 && (
        <Count>{numActiveFilters > 9 ? "9+" : numActiveFilters}</Count>
      )}
    </FiltersButton>
  );

  return (
    <Bar className={className}>
      <FiltersChips
        filtersActions={filtersActions}
        filtersSelectors={filtersSelectors}
        hideFilterTypes={hideFilterTypes}
        filterTypeToTitle={filterTypeToTitle}
        isSingleRowView={true}
        hideFiltersButton={true}
        style={style}
        extraComponent={
          <>
            {filtersButton}
            {extraComponent}
          </>
        }
      />
      <Drawer
        open={drawerOpen}
        size={320}
        closable={false}
        onClose={() => setDrawerOpen(false)}
        styles={{ body: { padding: 20 } }}
        destroyOnHidden={false}
      >
        <FiltersColumn
          filtersActions={filtersActions}
          filtersSelectors={filtersSelectors}
          filterTypeToTitle={filterTypeToTitle}
          hideFilterTypes={hideFilterTypes}
        />
      </Drawer>
    </Bar>
  );
};

export default React.memo(NewLookFiltersBar);

const pill = css`
  height: 32px;
  border-radius: ${(p) => p.theme.radii.control}px;
  border: ${(p) => getNewLookChipColors(p.theme).border};
  background: ${(p) => getNewLookChipColors(p.theme).background};
  transition: border-color 150ms, background-color 150ms, color 150ms;
`;

const FiltersButton = styled.button<{ $active: boolean }>`
  ${pill};
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 36px;
  padding: 0 10px;
  cursor: pointer;
  color: ${(p) =>
    p.$active ? p.theme.colors.primaryBlue : p.theme.colors.secondaryText};
  @media (hover: hover) {
    &:hover {
      color: ${(p) => p.theme.colors.titleColor};
      border-color: ${(p) => p.theme.colors.lightBorderGrey};
      background: ${(p) => p.theme.colors.cardLightBgColor};
    }
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 1px;
  }
`;
const Count = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.primaryBlue};
  color: ${(p) => p.theme.colors.buttonText};
  font-size: 11px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  font-variant-numeric: tabular-nums;
`;

const ACTIVE_CHIP = `[style*="color: rgb(255, 255, 255)"], [data-chip-active]`;

const Bar = styled.div`
  min-width: 0;
  /* Desktop: let a long row of chips wrap instead of running off the page. */
  @media (min-width: 801px) {
    > div > div {
      flex-wrap: wrap;
      row-gap: 8px;
    }
  }
  #filter-time-frame .ant-picker,
  .ant-select:not(${ACTIVE_CHIP}) {
    height: 32px !important;
    box-shadow: none !important;
    transition: border-color 150ms, background-color 150ms;
  }
  @media (hover: hover) {
    #filter-time-frame .ant-picker:not(${ACTIVE_CHIP}):hover,
    .ant-select:not(${ACTIVE_CHIP}):hover {
      border-color: ${(p) => p.theme.colors.lightBorderGrey} !important;
      background: ${(p) => p.theme.colors.cardLightBgColor} !important;
    }
  }
  .ant-select-placeholder p,
  .ant-select-content-value {
    font-size: 13px;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
  .ant-select:not(${ACTIVE_CHIP}) {
    .ant-select-placeholder p,
    .ant-select-content-value {
      color: ${(p) => p.theme.colors.primaryText};
    }
  }
  #filter-time-frame .ant-picker {
    padding: 0 12px;
    input {
      font-size: 13px;
      background: transparent !important;
    }
    .ant-picker-separator,
    .ant-picker-suffix {
      color: ${(p) => p.theme.colors.secondaryText};
    }
  }
`;
